import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetService } from "./useGetService";
import { API, DashboardTabs } from "../constants";

// Third party
// import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { usePostService } from "./usePostService";
import { toast } from "react-toastify";
import {
  EServiceRequestCategory,
  EServiceRequestType,
  IServiceRequest,
  ITmResponse,
} from "../types/global.types";
import { useArrivalDepartureData } from "./useArrivalDepartureData";
import moment from "moment";
import { unflattenObject } from "../shared/utils";

export const useAdminDataGrid = (
  startDate: Date,
  endDate: Date,
  selectedTab: string,
  selectedPort: string,
  appliedStartDate: Date,
  appliedEndDate: Date
) => {
  const { postData } = usePostService();

  const [rows, setRows] = useState<any[]>([]);
  const [isRowsLoading, setRowsLoading] = useState(false);

  // API CALL
  const brand = selectedPort;
  const {
    response,
    isLoading,
    handleSearchApply,
    refreshingText,
    error,
    detailedError,
  } = useArrivalDepartureData({
    startDate,
    endDate,
    selectedTab,
    destinationCity: selectedPort,
  });

  const assignmentStartDate = moment(appliedStartDate); //.subtract(7, "day");
  const assignmentEndDate = moment(appliedEndDate);
  // FETCH ASSIGNMENT

  const isDeparture = selectedTab === DashboardTabs.DEPARTURE;

  const serviceRequestUrl = `${API.CARNIVAL_ADMIN.GET_SERVICE_REQUEST_BY_VENDOR_ID
    }?startDate=${assignmentStartDate.format(
      "DD/MM/YYYY"
    )}&endDate=${assignmentEndDate.format("DD/MM/YYYY")}&requestCategory=${isDeparture ? EServiceRequestCategory.DEPARTURE : EServiceRequestCategory.ARRIVAL}`

  const {
    response: serviceRequests,
    isLoading: isAssignedDataLoading,
    refetch: serviceRequestRefetch,
    manualUpdateResp: manualUpdateRespServiceRequests,
  } = useGetService(serviceRequestUrl);

  // FETCH CREATED MANUAL REQUEST DETAILS
  const {
    response: manualRequestDetails,
    isLoading: ismanualRequestDetailsLoading,
    refetch: mannualRequestRefecth,
  } = useGetService(`${API.VENDOR_SERVICE_REQUEST.CREATE_MANUAL_REQUEST}s`);

  const assignedData = useMemo(() => {
    if (
      isAssignedDataLoading ||
      !Array.isArray(serviceRequests?.data) ||
      !response?.data
    ) {
      return { data: [] };
    }

    const filteredData = serviceRequests.data?.filter((x: IServiceRequest) =>
      isDeparture
        ? x.requestCategory === EServiceRequestCategory.DEPARTURE
        : x.requestCategory === EServiceRequestCategory.ARRIVAL
    );

    return { data: filteredData || [] as IServiceRequest[] };
  }, [response, serviceRequests, isAssignedDataLoading, isDeparture]);

  useEffect(() => {
    let interval = setInterval(() => {
      serviceRequestRefetch(
        serviceRequestUrl,
        true
      );
    }, 5 * 60 * 1000); // 5 minutes in ms
    return () => {
      clearInterval(interval);
    };
  }, [serviceRequestUrl]);

  const getArrivedInfo = useCallback(
    (tm: ITmResponse) => {
      if (isAssignedDataLoading || !assignedData?.data) return false;

      const isTmArrived = assignedData?.data.some((serviceRequest: any) => {
        return (
          serviceRequest.requestType ===
          EServiceRequestType.SHIPBOARD_TM_ONBOARDING &&
          serviceRequest.tmId === tm.EmpId
        );
      });

      return isTmArrived;
    },

    [assignedData, isAssignedDataLoading]
  );

  const getUnManifestedTmScans = useCallback(() => {
    const unManifestedTms: any[] = [];
    if (isAssignedDataLoading || !assignedData?.data) return [];

    assignedData?.data?.forEach((serviceRequest: IServiceRequest) => {
      if (!serviceRequest.isUnManifested) return;

      const unManifestedTm = {
        id: serviceRequest.tmId,
        manualRequestId: serviceRequest.id,
        SCH_ARVL_DT: serviceRequest.arrivalDate,
        SCH_ARVL_TIME: serviceRequest.arrivalTime
          ? serviceRequest.arrivalTime
          : "",
        EMP_ID: serviceRequest.tmId,
        isUrgentTransportRequest: false,
        FULL_NAME: serviceRequest.tmName || "Nil",
        DEPT: "Nil",
        HOTEL_NAME: "Nil",
        empName: "",
        Name: serviceRequest.tmName,
        assignedInfo: [serviceRequest],
        Ship: {
          Name: "Nil",
        },
        FlagsOut: [],
        Port: {},
        isMeet_and_greetScanned: false,
        isTransportationScanned: false,
        isHotelScanned: false,
        isPortAgent: false,
        isArrivedToShip: getArrivedInfo({
          EmpId: serviceRequest.tmId,
        } as ITmResponse),
      };

      unManifestedTms.push(unManifestedTm);
    });

    return unManifestedTms;
  }, [assignedData, isAssignedDataLoading, getArrivedInfo]);

  const dataHandling = useCallback(
    (resData: ITmResponse[]) => {
      let newData: any = [];
      setRowsLoading(true);

      const resDataTmIds = new Set(resData.map((x) => x.EmpId));

      if (resData.length > 0) {
        const data = _.cloneDeep(resData);
        data.forEach((x) => {
          const y = {
            ...x,
            id: x.EmpId, //uuidv4(),
            isMeet_and_greetScanned: false,
            isTransportationScanned: false,
            isHotelScanned: false,
            isPortAgent: false,
            isArrivedToShip: getArrivedInfo(x),
            isManualRequestCreated:
              (!ismanualRequestDetailsLoading &&
                manualRequestDetails?.data &&
                manualRequestDetails?.data.some(
                  (v: any) => v.tmId === x.EmpId
                )) ||
              false,
            assignedInfo:
              (!isAssignedDataLoading &&
                assignedData?.data &&
                assignedData?.data.filter((v: any) => v.tmId === x.EmpId)) ||
              [],
          };
          newData.push(y);
        });
      }

      if (
        !isAssignedDataLoading &&
        assignedData?.data &&
        Array.isArray(assignedData.data)
      ) {
        const assignedItemsByTmId = new Map<string, any[]>();
        assignedData.data?.forEach((assignedItem: any) => {
          if (!resDataTmIds.has(assignedItem.tmId)) {
            if (!assignedItemsByTmId.has(assignedItem.tmId)) {
              assignedItemsByTmId.set(assignedItem.tmId, []);
            }
            assignedItemsByTmId.get(assignedItem.tmId)!.push(assignedItem);
          }
        });

        assignedItemsByTmId.forEach((assignedItems, tmId) => {
          const mainAssignedItem = assignedItems[0];
          const unflattenedItem = unflattenObject(mainAssignedItem);

          const mergedItem = {
            ...unflattenedItem,
            EmpId: tmId,
            id: tmId,
            isMeet_and_greetScanned: false,
            isTransportationScanned: false,
            isHotelScanned: false,
            isPortAgent: false,
            isArrivedToShip: getArrivedInfo({
              EmpId: tmId,
            } as ITmResponse),
            isManualRequestCreated:
              (!ismanualRequestDetailsLoading &&
                manualRequestDetails?.data &&
                manualRequestDetails?.data.some(
                  (v: any) => v.tmId === tmId
                )) ||
              false,
            assignedInfo: assignedItems,
          };

          newData.push(mergedItem);
        });
      }

      // const unManifestedTms = getUnManifestedTmScans();

      const filteredResponseData = newData.filter((item: any) => {
        if (item.assignedInfo?.length) {
          return true
        }

        const shipName = item?.FutureAssignments?.ShipName;

        if (
          !shipName ||
          item.TMDetails?.Department === "SUPERNUM" ||
          item.TMDetails?.Department === "CCL RIDING TEAM"
        ) {
          return false;
        }

        if (typeof shipName === "string") {
          return !shipName.toLowerCase().includes("ccl -");
        }

        return true;
      });

      setRowsLoading(false);

      return filteredResponseData;
    },
    [
      assignedData?.data,
      isAssignedDataLoading,
      ismanualRequestDetailsLoading,
      getUnManifestedTmScans,
      getArrivedInfo,
      manualRequestDetails?.data,
      isDeparture,
    ]
  );

  useEffect(() => {
    setRowsLoading(isLoading || isAssignedDataLoading);

    // if (!isLoading && Array.isArray(response?.data)) {
    //   const processedData = dataHandling(response.data);
    //   setRows(processedData);
    // }

    if (!isLoading && Array.isArray(response?.data)) {


      const processedData = dataHandling(response?.data || []);
      setRows(processedData);
    }

    // Optional: handle case where response is empty
    if (!isLoading && !response?.data?.length) {
      setRows([]); // explicitly clear if no result
    }
  }, [response, isLoading, isAssignedDataLoading, dataHandling]);

  const refetchData = () => {
    serviceRequestRefetch(
      serviceRequestUrl,
    );
  };

  const handleArriveClickedCall = async (payload: any) => {
    try {
      const resp = await postData(
        API.CARNIVAL_ADMIN.CREATE_SERVICE_REQUEST,
        payload
      );

      toast.success("Acknowledged successfully by shipboard! ");
      const dataIndex = rows.findIndex(
        (x: any) => x.EmpId === payload.assignedDetails?.[0]?.tmId
      );
      const currentData = [...rows] as any;
      currentData[dataIndex].isArrivedToShip = true;

      if (!currentData[dataIndex].assignedInfo || !Array.isArray(currentData[dataIndex].assignedInfo)) {
        currentData[dataIndex].assignedInfo = [];
      }

      if (resp.data && Array.isArray(resp.data)) {
        manualUpdateRespServiceRequests({ data: [...serviceRequests?.data || [], ...resp.data] });
      }

      currentData[dataIndex].assignedInfo.push(...resp.data || []);
      setRows(currentData);
    } catch (err) {
      console.error(err);
      toast.error("Failed!");
    }
  };

  const handleCreateManualRequest = (resInfo: any) => {
    mannualRequestRefecth(
      `${API.VENDOR_SERVICE_REQUEST.CREATE_MANUAL_REQUEST}s`
    );

    // const data = [...rows];
    // data.map((x: any) => {
    //   x.isManualRequestCreated =
    //     x.isManualRequestCreated === false
    //       ? resInfo.data.some((y: any) => y.tmId === x.EmpId)
    //       : x.isManualRequestCreated;
    //   return x;
    // });
    // setRows(data);
    // console.log("handleCreateManualRequest", data, resInfo);
  };

  return {
    rows,
    isRowsLoading: isRowsLoading || isAssignedDataLoading,
    refetchData,
    assignedData,
    handleArriveClickedCall,
    handleCreateManualRequest,
    handleSearchApply,
    refreshingText,
    error,
    detailedError,
    isAssignedDataLoading
  };
};
